import React, { useState } from 'react';

import { dashboardInvoiceApis, invoiceApis } from '@apis/next/invoiceApis';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import {
  BookingDetailsContext,
  type BookingDetailsContextValues,
} from '@components/admin/booking/helpers';
import { InvoiceFormDialog } from '@components/admin/booking/InvoiceFormDialog';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { SpinnerModal } from '@components/SpinnerModal';
import { invoiceStrings } from '@data/invoice';
import { useBookingInquiryDetailByD2dNo } from '@hooks/queries/dashboard/useBookingInquiryDetailByD2dNo';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useBlobURL } from '@hooks/useBlobURL';
import { useDialogKey } from '@hooks/useDialogKey';
import { useSearch } from '@hooks/useSearch';
import { AdminInquiryDetailHelper } from '@models/BookingInquiry';
import { InvoiceHelper } from '@models/next/InvoiceHelper';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { foldMessagesEither } from '@utils/errors';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDateTime, stringifySearch, toJST } from '@utils/types';
import { useShallowMemo } from '@utils/useShallowMemo';
import * as t from 'io-ts';
import { Document, Page } from 'react-pdf';
import * as h from 'tyrann-io';

const useStyles = makeMuiStyles((theme, common) => ({
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  container: {
    ...common.flexCenter,
    margin: 16,
    marginBottom: 200,
  },
  operationPanel: {
    ...common.flexCenter,
    ...common.middleText,
    position: 'fixed',
    bottom: 0,
    background: theme.palette.primary.contrastText,
    padding: '10px 45px',

    width: '100%',
    boxShadow: '0px -4px 4px rgba(0, 0, 0, 0.25);',
  },
  operationBox: {
    ...common.flexInline,
    ...common.middleText,
    background: theme.palette.primary.contrastText,
    padding: '10px 45px',
    maxWidth: 750,
    width: '100%',
  },
  info: {
    flex: 1,
    ...common.flexColumn,
  },
  operation: {
    ...common.flexCenter,
    lineHeight: 80,
    '& button': {
      marginLeft: 30,
    },
  },
}));

const strings = createStrings({
  ja: {
    title: '請求書',
    update: '修正',
    send: '送信',
    delete: '削除',
    unlock: 'ロック解除',
    errorHasSend:
      '請求書が既に送信されましたので、ロック解除できませんでした。新しい請求書を作成してください。',
    errorHasPay:
      'お客さんが既に入金しましたので、ロック解除できませんでした。新しい請求書を作成してください。',
    errorTimeout:
      'このJOBの期限が過ぎたので、ロック解除できませんでした。新しい請求書を作成してください。',
    errorNotAuthorized: () => (
      <>
        申し訳ありませんが、ロックを解除する権限がありません。
        <br />
        ロックを解除するには、Adminに連絡してください。
        <br />
      </>
    ),
    unlockMessage: () => (
      <>本当に管理員の権限を利用し、既に送信した請求書を取り下げて修正可能なようにしますか？</>
    ),
  },
});

const searchType = t.type({
  id: h.number().cast(),
  d2dNo: t.string,
});

export const InvoiceDetail: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(searchType);

  const user = useUser();
  const [invoiceFormOpen, setInvoiceFormOpen] = useState(false);
  const invoiceDialogKey = useDialogKey(invoiceFormOpen);

  const { confirm, renderDialog } = useConfirmDialog();

  const invoiceQuery = dashboardInvoiceApis.useGet('/dashboard/invoice/:id', {
    params: {
      id: search.id,
    },
  });

  const bookingInquiry = useBookingInquiryDetailByD2dNo(search.d2dNo);
  const preAlert = dashboardPreAlertApis.useGet('/dashboard/pre-alert/:d2d_no', {
    params: {
      d2d_no: search.d2dNo,
    },
  });

  const canEdit =
    user.data &&
    preAlert.data &&
    invoiceQuery.data &&
    InvoiceHelper.canUpdateInvoice(user.data, preAlert.data, invoiceQuery.data);
  const canUnlock =
    user.data && preAlert.data && AdminInquiryDetailHelper.canUnlock(user.data, preAlert.data);

  const invoicePdf = invoiceApis.useGet(
    '/invoice/v3/:visit_id/download',
    {
      params: {
        visit_id: invoiceQuery.data?.visit_id!,
      },
    },
    {
      enabled: invoiceQuery.isSuccess,
      staleTime: Infinity,
    },
  );
  const invoiceUrl = useBlobURL(invoicePdf.data);

  const patchInvoice = dashboardInvoiceApis.usePatch(
    '/dashboard/invoice/:id',
    {
      params: {
        id: search.id,
      },
    },
    {
      onSuccess() {
        invoicePdf.invalidate();
        invoiceQuery.invalidate();
      },
    },
  );

  const bookingDetailsContextValues: BookingDetailsContextValues = useShallowMemo(() => ({
    preAlert: preAlert.data!,
    bookingInquiry: bookingInquiry.data!,
    user: user.data!,
  }));

  const renderOperationPanel = () => {
    return (
      canUnlock &&
      canEdit &&
      invoiceQuery.data && (
        <div className={classes.operationPanel}>
          <div className={classes.operationBox}>
            <div className={classes.info}>
              <div>
                作成者: {invoiceQuery.data.creator.last_name} {invoiceQuery.data.creator.first_name}
              </div>
              <div>
                送信時間:{' '}
                {invoiceQuery.data.send_time ? formatDateTime(invoiceQuery.data.send_time) : '-'}
              </div>
              <div>送信先: {invoiceQuery.data.client_name || '-'}</div>
              <div>Latest Update: {toJST(new Date(invoiceQuery.data.updated_at))}</div>
              <div>
                <a
                  target="_blank"
                  href={
                    url('/admin/booking/PrintInvoiceLog') + '?' + stringifySearch({ id: search.id })
                  }
                  className={classes.link}
                >
                  {invoiceStrings.changeLog}
                </a>
              </div>
            </div>
            {invoiceQuery.data.invoice_status === '発行済' ? (
              <div className={classes.operation}>
                <Button
                  color={foldMessagesEither(
                    canUnlock,
                    () => 'grey',
                    () => 'warning',
                  )}
                  size="small"
                  shape="round"
                  text={strings.unlock}
                  type="button"
                  style={{ width: 88 }}
                  onClick={async () => {
                    foldMessagesEither(
                      canUnlock,
                      async ({ messages }) => {
                        await confirm({
                          title: strings.unlock,
                          message: messages,
                        });
                      },
                      async () => {
                        if (
                          (await confirm({
                            title: strings.unlock,
                            message: strings.unlockMessage(),
                          })) === 'confirmed'
                        ) {
                          await patchInvoice.mutateAsync({
                            invoice_status: '修正中',
                          });
                        }
                      },
                    );
                  }}
                />
              </div>
            ) : (
              <div className={classes.operation}>
                <Button
                  color={foldMessagesEither(
                    canEdit,
                    () => 'grey',
                    () => 'primary',
                  )}
                  size="small"
                  text={strings.update}
                  type="button"
                  shape="round"
                  style={{ width: 88 }}
                  onClick={() => {
                    canEdit &&
                      foldMessagesEither(
                        canEdit,
                        async ({ messages }) => {
                          await confirm({
                            title: strings.unlock,
                            message: messages,
                          });
                        },
                        async () => {
                          setInvoiceFormOpen(true);
                        },
                      );
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )
    );
  };

  return (
    <Layout
      hideHeader
      hideFooter
      hideCookiePolicy
      title={strings.title}
      classes={{
        background: classes.background,
      }}
      loading={
        user.isLoading ||
        invoiceQuery.isLoading ||
        bookingInquiry.isLoading ||
        invoicePdf.isLoading ||
        preAlert.isLoading
      }
      errors={[
        user.error,
        invoiceQuery.error,
        bookingInquiry.error,
        invoicePdf.error,
        preAlert.error,
      ]}
    >
      {() => (
        <BookingDetailsContext.Provider value={bookingDetailsContextValues}>
          <SpinnerModal open={patchInvoice.isLoading} />

          <div className={classes.container}>
            <Document key={invoiceUrl} file={invoiceUrl} loading="">
              <Page pageNumber={1} width={900} />
            </Document>
          </div>
          {renderOperationPanel()}

          <InvoiceFormDialog
            key={invoiceDialogKey}
            type="update"
            open={invoiceFormOpen}
            onClose={() => setInvoiceFormOpen(false)}
            id={search.id}
          />

          {renderDialog()}
        </BookingDetailsContext.Provider>
      )}
    </Layout>
  );
};
